import React, { useRef } from 'react';
import { Printer } from 'lucide-react';
import { currencyCodeToSymbolMap } from '../apiHelper';

const ThermalReceiptPerf = ({ order }) => {
    const receiptRef = useRef(null);

    if (!order) return null;

    // Use totalproduct from payload and items mapping
    const totalAmount = Number(order.totalproduct || 0);
    const cashPaid = Number(order.cash_sale_amount || 0);
    const creditExtended = Number(order.credit_sale_amount || 0);
    const currency = currencyCodeToSymbolMap[order.currency] || '₦';

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert("Print blocked. Please allow pop-ups for this site.");
            return;
        }

        const content = receiptRef.current?.innerHTML || '';
        const fullHtml = `
            <html>
            <head>
                <title>Receipt - ${order.idempotency_key}</title>
                <script src="https://cdn.tailwindcss.com"></script>
                <style>
                    @page { size: auto; margin: 5mm; }
                    body { font-family: monospace; font-size: 10px; padding: 2mm; background: white; color: black; }
                    .border-b-dashed { border-bottom: 1px dashed #000; }
                    .border-t-dashed { border-top: 1px dashed #000; }
                    .flex { display: flex; }
                    .justify-between { justify-content: space-between; }
                    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                </style>
            </head>
            <body>
                <div style="max-width: 80mm; margin: 0 auto;">${content}</div>
            </body>
            </html>`;

        printWindow.document.write(fullHtml);
        printWindow.document.close();
        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 300);
        };
    };

    return (
        <div className="flex flex-col items-center">
            <div
                ref={receiptRef}
                className="w-full max-w-[300px] bg-white p-4 font-mono text-[11px] text-gray-900 leading-tight"
            >
                <div className="text-center mb-4">
                    <h3 className="font-bold text-sm uppercase">{order.store?.identifier || 'Store'}</h3>
                    <p className="mt-1">{new Date(order.placed_on).toLocaleString()}</p>
                    <div className="border-b-dashed border-gray-400 my-2"></div>
                    <p className="font-bold uppercase tracking-wider">Sales Record</p>
                    <p className="text-[10px]">Ref: {order.idempotency_key}</p>
                </div>

                <div className="space-y-3">
                    {/* Access order.items based on new response schema */}
                    {(order.items || []).map((item, idx) => (
                        <div key={idx} className="flex flex-col">
                            <div className="flex justify-between font-bold uppercase">
                                <span className="w-2/3">{item.catentry?.productname}</span>
                                <span>{currency}{Number(item.totalproduct).toLocaleString()}</span>
                            </div>
                            <div className="text-[10px] text-gray-600">
                                {item.quantity} units @ {currency}{Number(item.price).toLocaleString()}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="border-t-dashed border-gray-400 mt-5 pt-3 space-y-1">
                    <div className="flex justify-between font-bold text-sm">
                        <span>TOTAL PAYABLE</span>
                        <span>{currency}{totalAmount.toLocaleString()}</span>
                    </div>
                    
                    {/* Enhanced Footer for Split Payments */}
                    <div className="flex justify-between text-[10px]">
                        <span>CASH RECEIVED</span>
                        <span>{currency}{cashPaid.toLocaleString()}</span>
                    </div>
                    {creditExtended > 0 && (
                        <div className="flex justify-between text-[10px] text-rose-700">
                            <span>CREDIT BALANCE</span>
                            <span>{currency}{creditExtended.toLocaleString()}</span>
                        </div>
                    )}
                </div>

                <div className="mt-8 space-y-1 text-[10px]">
                    <p className="uppercase font-medium">Attendant: {order.creator?.first_name} {order.creator?.last_name || ''}</p>
                    <p className="uppercase font-medium">Customer: {order.customer?.first_name} {order.customer?.last_name || ''}</p>
                    <p className="uppercase font-bold mt-2">
                        Status: {creditExtended > 0 ? 'PARTIAL / CREDIT' : 'PAID IN FULL'}
                    </p>
                    <div className="border-b-dashed border-gray-400 my-3"></div>
                    <p className="text-center italic font-bold">*** THANK YOU ***</p>
                </div>
            </div>

            <button
                onClick={handlePrint}
                className="mt-6 w-full max-w-[300px] flex items-center justify-center gap-2 bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-md"
            >
                <Printer size={18} /> Print Sales Record
            </button>
        </div>
    );
};

export default ThermalReceiptPerf;